import { useState } from "react";
import { MoveHorizontal, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { PhotoSlot } from "./PhotoSlot";
import { Reveal, Eyebrow } from "./Reveal";
import { BEFORE_AFTER, GALLERY } from "@/content/site";

export const Slider = ({ data = BEFORE_AFTER }) => {
  const [pos, setPos] = useState(50);
  return (
    <div className="relative aspect-square w-full overflow-hidden chamfer border border-[var(--line)] select-none bg-[var(--steel)]" data-testid="before-after-slider">
      <div className="absolute inset-0"><PhotoSlot {...data.after} tone="light" label="After photo to be added" /></div>
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <PhotoSlot {...data.before} label="Before photo to be added" />
      </div>
      <span className="absolute top-4 left-4 bg-black/80 px-3 py-1.5 font-mono text-xs uppercase tracking-[0.2em]">Before</span>
      <span className="absolute top-4 right-4 bg-[var(--red)] px-3 py-1.5 font-mono text-xs uppercase tracking-[0.2em]">After</span>
      <div className="absolute inset-y-0 w-[2px] bg-white pointer-events-none" style={{ left: `${pos}%` }} aria-hidden>
        <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white text-[var(--ink)] flex items-center justify-center shadow-xl">
          <MoveHorizontal className="h-5 w-5" />
        </span>
      </div>
      <input type="range" min="0" max="100" value={pos} onChange={(e) => setPos(Number(e.target.value))}
        aria-label="Drag to compare before and after" data-testid="before-after-range"
        className="absolute inset-0 h-full w-full opacity-0 cursor-ew-resize" />
    </div>
  );
};

export default function BeforeAfter() {
  return (
    <section className="py-20 md:py-32 border-b border-[var(--line)]" data-testid="results-section">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-10 md:mb-14">
          <Reveal className="lg:col-span-7">
            <Eyebrow n="02">From the bench</Eyebrow>
            <h2 className="mt-5 font-display font-black uppercase text-5xl md:text-7xl leading-[0.9]">Before &amp; after</h2>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.1}>
            <p className="text-sm md:text-base text-[var(--grey)] leading-relaxed">Genuine photographs from real repairs on the workshop bench. Drag the handle to compare. More results are on the case studies page.</p>
          </Reveal>
        </div>
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <Reveal className="lg:col-span-7"><Slider /></Reveal>
          <Reveal className="lg:col-span-5" delay={0.1}>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--red)]">Case study 001 · Casing replacement</p>
            <h3 className="mt-4 font-display font-black uppercase text-4xl md:text-6xl leading-[0.9]" data-testid="before-after-title">{BEFORE_AFTER.title}</h3>
            <p className="mt-5 text-sm md:text-base text-[var(--grey)] leading-relaxed max-w-md">{BEFORE_AFTER.summary}</p>
            <ul className="mt-6 flex flex-wrap gap-2">
              {["Strip down", "Clean", "Regrease", "Rebuild"].map((t) => (
                <li key={t} className="border border-[var(--line)] px-3 py-1.5 font-mono text-xs uppercase tracking-[0.16em] text-[var(--grey)]">{t}</li>
              ))}
            </ul>
            <p className="mt-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.18em] text-[var(--mute)]"><MoveHorizontal className="h-4 w-4 text-[var(--red)]" aria-hidden /> Drag the slider to compare</p>
            <Link to="/case-studies/milwaukee-m18-impact-wrench-casing-replacement" className="group mt-8 inline-flex items-center gap-3 font-display font-extrabold uppercase tracking-[0.14em] text-sm border-b-2 border-[var(--red)] pb-1" data-testid="home-case-study-link">
              Read the full case study <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden />
            </Link>
          </Reveal>
        </div>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="repair-gallery">
          {GALLERY.map((g, i) => (
            <Reveal key={g.caption} delay={i * 0.08}>
              <figure className="group">
                <div className="aspect-square overflow-hidden border border-[var(--line)] chamfer-sm">
                  <div className="h-full w-full transition-transform duration-700 group-hover:scale-[1.04]"><PhotoSlot {...g} /></div>
                </div>
                <figcaption className="mt-3 flex justify-between font-mono text-xs uppercase tracking-[0.18em] text-[var(--mute)]">
                  <span>{g.caption}</span><span className="text-[var(--red)]">0{i + 1}</span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
